import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Mic, Play, Pause, Settings, Instagram, Twitter, Youtube, Globe, Share2 } from 'lucide-react';
import { audioApi, comboApi, getAudioFileUrl } from '@/lib/api';
import { AUDIO_COLORS } from '@/lib/constants';
import { AudioVisualBadge } from '@/components/audio/AudioVisuals';
import { getComboColorHex } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import { shareAudio } from '@/lib/share';

const PerfilPage = () => {
  const { user, isAuthenticated, login } = useAuth();
  const navigate = useNavigate();
  const [myAudios, setMyAudios] = useState([]);
  const [myCombos, setMyCombos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [playingId, setPlayingId] = useState(null);
  const [audioEl, setAudioEl] = useState(null);

  const [profileData, setProfileData] = useState({
    bio: user?.bio || '',
    instagram: user?.instagram || '',
    twitter: user?.twitter || '',
    youtube: user?.youtube || '',
    website: user?.website || ''
  });

  // Sincronizar estados cuando el usuario carga de forma asíncrona
  useEffect(() => {
    if (user) {
      setProfileData({
        bio: user.bio || '',
        instagram: user.instagram || '',
        twitter: user.twitter || '',
        youtube: user.youtube || '',
        website: user.website || ''
      });
    }
  }, [user]);

  useEffect(() => {
    if (isAuthenticated && user) {
      loadMyAudios();
      loadMyCombos();
    } else {
      setLoading(false);
    }
  }, [isAuthenticated, user]);

  const loadMyAudios = async () => {
    try {
      const data = await audioApi.getFeed({ limit: 50 });
      const mine = data.filter(a => a.user_id === user.user_id);
      // Ordenar por fecha de creación (más recientes primero)
      mine.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      setMyAudios(mine);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadMyCombos = async () => {
    try {
      const data = await comboApi.getMyCombos();
      setMyCombos(data);
    } catch (error) {
      console.error('Error cargando combos:', error);
    }
  };

  const togglePlay = (audio) => {
    if (playingId === audio.audio_id) {
      audioEl?.pause();
      setPlayingId(null);
    } else {
      if (audioEl) audioEl.pause();
      const newAudio = new Audio(getAudioFileUrl(audio.audio_url));
      newAudio.play();
      newAudio.onended = () => setPlayingId(null);
      setAudioEl(newAudio);
      setPlayingId(audio.audio_id);
    }
  };

  const shareProfile = async () => {
    const url = `${window.location.origin}/perfil/${user?.user_id}`;
    if (navigator.share) {
      await navigator.share({
        title: `${user?.name} en AudioToo`,
        text: `Escucha mis audios en AudioToo 🎙️`,
        url
      });
    } else {
      await navigator.clipboard.writeText(url);
      alert('Link copiado!');
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
        <div className="text-center">
          <p className="text-6xl mb-4">👤</p>
          <h2 className="text-xl font-bold text-white mb-2">Inicia sesión</h2>
          <p className="text-white/60 mb-4">Para ver tu perfil y audios</p>
          <button 
            onClick={login}
            className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-6 py-3 rounded-full"
          >
            Entrar con Google
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/5 border-b border-white/10">
        <div className="max-w-lg mx-auto px-4 py-3 flex items-center justify-between">
          <button onClick={() => navigate('/')} className="text-white/60 p-2 -ml-2">
            <ArrowLeft size={24} />
          </button>
          <h1 className="text-white font-bold">Mi Perfil</h1>
          <button onClick={() => navigate('/perfil/ajustes')} className="text-white/60 p-2">
            <Settings size={20} />
          </button>
        </div>
      </header>

      <main className="max-w-lg mx-auto px-4 py-6 space-y-6">
        {/* Perfil Card */}
        <div className="bg-white/5 backdrop-blur rounded-2xl overflow-hidden border border-white/10">
          {/* Banner/Fondo */}
          <div className="h-24 bg-gradient-to-r from-purple-600 via-pink-500 to-orange-400" />
          
          {/* Avatar y datos */}
          <div className="px-6 pb-6 -mt-12">
            <div className="flex items-end justify-between mb-4">
              {user.picture ? (
                <img 
                  src={user.picture} 
                  alt="" 
                  className="w-24 h-24 rounded-2xl border-4 border-slate-900 shadow-xl object-cover" 
                />
              ) : (
                <div className="w-24 h-24 rounded-2xl border-4 border-slate-900 bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center shadow-xl">
                  <span className="text-4xl text-white">{user.name?.charAt(0)}</span>
                </div>
              )}
              
              <div className="flex gap-2">
                <button 
                  onClick={shareProfile}
                  className="bg-white/10 text-white p-2 rounded-xl"
                >
                  <Share2 size={18} />
                </button>
              </div>
            </div>
            
            <h2 className="text-xl font-bold text-white">{user.name}</h2>
            <p className="text-white/50 text-sm">@{user.name?.split(' ')[0]?.toLowerCase()}</p>
            
            {profileData.bio ? (
              <p className="text-white/70 text-sm mt-3">{profileData.bio}</p>
            ) : (
              <p className="text-white/40 text-sm mt-3 italic">Añade tu bio desde ⚙️ Configuración</p>
            )}
            
            {/* Stats */}
            <div className="flex gap-6 mt-4">
              <div className="text-center">
                <p className="text-2xl font-bold text-white">{myAudios.length}</p>
                <p className="text-white/50 text-xs">Audios</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-white">0</p>
                <p className="text-white/50 text-xs">Seguidores</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-white">0</p>
                <p className="text-white/50 text-xs">Siguiendo</p>
              </div>
            </div>
            
            {/* Redes sociales */}
            <div className="flex gap-3 mt-4">
              {profileData.instagram && (
                <a href={`https://instagram.com/${profileData.instagram}`} target="_blank" rel="noopener noreferrer" className="text-pink-400">
                  <Instagram size={20} />
                </a>
              )}
              {profileData.twitter && (
                <a href={`https://twitter.com/${profileData.twitter}`} target="_blank" rel="noopener noreferrer" className="text-blue-400">
                  <Twitter size={20} />
                </a>
              )}
              {profileData.youtube && (
                <a href={`https://youtube.com/${profileData.youtube}`} target="_blank" rel="noopener noreferrer" className="text-red-500">
                  <Youtube size={20} />
                </a>
              )}
              {profileData.website && (
                <a href={profileData.website} target="_blank" rel="noopener noreferrer" className="text-white/60">
                  <Globe size={20} />
                </a>
              )}
            </div>
          </div>
        </div>

        {/* Acciones rápidas */}
        <div className="grid grid-cols-1 gap-3">
          <button
            onClick={() => navigate('/grabar')}
            className="bg-gradient-to-r from-red-500 to-pink-500 text-white py-4 rounded-2xl font-medium flex items-center justify-center gap-2"
          >
            <Mic size={20} />
            Grabar nuevo audio
          </button>
        </div>

        {/* Mis Audios */}
        <section>
          <h3 className="text-white font-bold text-lg mb-3">🎙️ Mis audios</h3>
          
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full" />
            </div>
          ) : myAudios.length === 0 ? (
            <div className="bg-white/5 backdrop-blur rounded-2xl p-8 border border-white/10 text-center">
              <p className="text-4xl mb-4">🎤</p>
              <p className="text-white/60">Aún no has grabado audios</p>
              <button 
                onClick={() => navigate('/grabar')}
                className="mt-4 bg-purple-500 text-white px-6 py-2 rounded-full text-sm"
              >
                Grabar mi primer audio
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {myAudios.map((audio) => {
                const colorData = AUDIO_COLORS[audio.color] || AUDIO_COLORS.blue;
                const isPlaying = playingId === audio.audio_id;
                
                return (
                  <div 
                    key={audio.audio_id}
                    onClick={() => togglePlay(audio)}
                    className="bg-white/5 backdrop-blur rounded-xl p-4 border border-white/10 flex items-center gap-3 cursor-pointer hover:bg-white/10 transition-colors"
                  >
                    <AudioVisualBadge
                      shape={audio.shape}
                      color={audio.color}
                      emoji={audio.emoji || colorData.emoji}
                      language={audio.language}
                      size="sm"
                    />
                    
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-medium text-sm flex items-center gap-1">
                        {colorData.emoji} {colorData.name}
                      </p>
                      <p className="text-white/50 text-xs">{audio.duration_seconds}s • {new Date(audio.created_at).toLocaleDateString()}</p>
                    </div>
                    
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isPlaying ? 'bg-green-500' : 'bg-white/10'}`}>
                      {isPlaying ? (
                        <Pause size={18} className="text-white" />
                      ) : (
                        <Play size={18} className="text-white ml-0.5" />
                      )}
                    </div>
                    
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        shareAudio(audio, window.location.origin);
                      }}
                      className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20"
                    >
                      <Share2 size={16} className="text-white" />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {/* Mis combos */}
        {myCombos.length > 0 && (
          <section>
            <h3 className="text-white font-bold text-lg mb-3">🎧 Mis combos</h3>
            <div className="space-y-3">
              {myCombos.map((combo) => {
                const comboHex = getComboColorHex(combo);
                return (
                <Link
                  key={combo.combo_id}
                  to={`/combo/${combo.combo_id}`}
                  className="bg-white/5 backdrop-blur rounded-xl p-4 border border-white/10 flex items-center gap-3 hover:bg-white/10 transition-colors"
                >
                  <div
                    className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${comboHex ? '' : 'bg-gradient-to-br from-purple-500 to-pink-500'}`}
                    style={comboHex ? { backgroundColor: comboHex } : undefined}
                  >
                    <span className="text-xl">🎧</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-medium text-sm truncate">{combo.name}</p>
                    <p className="text-white/50 text-xs">
                      {combo.rules?.length > 0
                        ? `${combo.rules.length} temática${combo.rules.length !== 1 ? 's' : ''} (auto)`
                        : `${combo.audios?.length || 0} audios`}
                    </p>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    combo.is_public ? 'bg-green-500/20 text-green-400' : 'bg-white/10 text-white/50'
                  }`}>
                    {combo.is_public ? 'Público' : 'Privado'}
                  </span>
                </Link>
                );
              })}
            </div>
          </section>
        )}
      </main>
    </div>
  );
};

export default PerfilPage;
